import { CommunityClient } from './CommunityClient';
import { AlertPresenter } from '../AlertBinder';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { Logger } from '../support/Logger';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { UserState } from '../user/UserState';
import { ICaremapAccess } from '../caremap/ICareMap';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { inviteAcceptedEvents } from './accessLevels';
import { SupportGroupDataProvider } from '../caremap/SupportGroupDataProvider';

export class UserAcceptInviteAlert {
    static inject = () => [
        AlertPresenter,
        CommunityClient,
        HistoryViewModel,
        MixpanelService,
        UserState,
        Logger,
        OrganizationsState,
        SupportGroupDataProvider
    ];
    constructor(
        private alert: AlertPresenter,
        private client: CommunityClient,
        private history: HistoryViewModel,
        private tracking: MixpanelService,
        private userState: UserState,
        private logger: Logger,
        private orgsState: OrganizationsState,
        private dataProvider: SupportGroupDataProvider
    ) {}

    show = async ({ token, access, patient }: { token: string; access: ICaremapAccess; patient?: string; }) => {
        let header = 'Accept Co-Owner Invite?';
        let message = 'To accept this invite, please review and agree to the following: 1) I certify that I have permission to manage a care profile on behalf of the Care Recipient. 2) The Care Recipient consents that Innerhive can use the data I enter on their behalf for the purpose of organizing and managing their care.';

        if (access === 'view-only'){
            header = 'Accept Viewer Invite?';
            message = `Become a member of Innerhive with viewer access and stay in the loop with care notifications and important updates.`;
            if (patient){
                message = `Become a member of ${patient}'s innerhive with viewer access and stay in the loop with care notifications and important updates.`;
            }
        }
        if (access === 'contributor'){
            header = 'Accept Contributor Invite?';
            message = `Add notes and recordings to ${patient ? `${patient}'s` : 'this'} innerhive. What you add is seen by the family members on this care map, and by their care agency if the family shares the care map with one. You'll see what the family shares with viewers, and everything you add.`;
        }

        const action = await this.alert.showAlertV2({
            header,
            message,
            wider: true,
            maskInReplay: true,
            options: [{
                id: 'decline',
                label: 'Decline',
                role: 'destructive'
            }, {
                id: 'consent',
                label: 'Accept'
            }]
        });

        if (action === 'consent') {
            const { careMap, careMapCategory, access: acceptedAccess, relationship } = await this.client.acceptedInvite(token);
            console.log('added caremap to caremap state', careMap.careMapId);

            // The shared notes are the landing place for a caregiver who has just joined a care
            // map. An agency user came here to reach the client's record, so take them to it.
            this.history.push(this.orgsState.isOrgTeamMember()
                ? `/care/${careMap.careMapId}`
                : `/notes/${careMap.careMapId}`);

            // the access the api granted, not the one the link claimed; the relationship as the picker's
            // id, as Contributor Invited tracks it, never the free-text label, which can name a person
            this.tracking.track(inviteAcceptedEvents[acceptedAccess], () => ({
                'Invite Email': this.userState.state.email,
                'Care Map Category': careMapCategory,
                ...(acceptedAccess === 'contributor' ? { Relationship: this.dataProvider.getRelationshipIdForLabel(relationship) } : {})
            }));
        } else {
            try {
                await this.client.declinedInvite(token);
            } catch (e: any) {
                // when accepting your OWN invite, returns a 409 err.status, err.errorCode
                // it's not rejected from an API perspective, should show the user the message
                // this will NOT go to Sentry (showAndLogError doesn't log to Sentry because it's < 500)
                if (e.status === 409 && e.errorCode === 'cannot-accept-own-invite') {
                    throw e;
                } else {
                    // ALL decline errors should be swallowed, but still send to SENTRY
                    if ((e?.status || 500) >= 500) {
                        this.logger.error(e);
                    }
                }
            }

            this.history.push('/');
        }
    };
}
